import type { WizardImageSlot } from "@faire/prototype-playground/workspace/yz/spe-ai-assisted-flow-mock/MockSpeImagesGrid";

import type { MockVariantRow } from "./variantTable";

const MOCK_SIZES = ["S", "M", "L"] as const;

const COLOR_NAME_POOL = [
  "Mist",
  "Slate",
  "Dune",
  "Cedar",
  "Fog",
  "Clay",
  "Bloom",
  "Harbor",
  "Pine",
  "Drift",
] as const;

const SWATCH_COLOR_POOL = [
  "#8B9A8E",
  "#6B7280",
  "#C4A574",
  "#9E4A44",
  "#9CA3AF",
  "#B07D62",
  "#C9A0B0",
  "#5B7C8F",
  "#6B7B5C",
  "#7A8FA3",
] as const;

const shuffle = <T>(items: readonly T[]): T[] => {
  const next = [...items];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = next[i];
    next[i] = next[j]!;
    next[j] = tmp!;
  }
  return next;
};

/**
 * Flare mock: one colorway per uploaded image (capped), × S/M/L sizes.
 * Names are randomized; photos map to the user's uploads only.
 */
export const buildVariantRowsFromImages = (
  images: WizardImageSlot[]
): MockVariantRow[] => {
  const colorCount = Math.min(
    Math.max(1, images.length),
    COLOR_NAME_POOL.length
  );
  const colorNames = shuffle(COLOR_NAME_POOL).slice(0, colorCount);

  return colorNames.flatMap((colorName, colorIdx) => {
    const photoUrl = images[colorIdx % images.length]?.url ?? "";
    return MOCK_SIZES.map((size) => ({
      key: `color-${colorIdx}-${size.toLowerCase()}`,
      displayName: `${colorName} / ${size}`,
      colorLabel: colorName,
      sizeLabel: size,
      swatchColor:
        SWATCH_COLOR_POOL[colorIdx % SWATCH_COLOR_POOL.length] ?? "#8B9A8E",
      photoUrl,
    }));
  });
};

export const cloneImageSnapshot = (
  images: WizardImageSlot[]
): WizardImageSlot[] => images.map((img) => ({ ...img }));
